import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";

// --- Types ---

export interface Permission {
  id: string;
  roleId: string;
  action: string;
  stage: string;
  jobTypeId: string;
}

export interface RoleSummary {
  id: string;
  tenantId: string;
  name: string;
  /** Stable per-tenant key (Role.slug) — used as the value written by role
   *  pickers so a later rename can't orphan the reference. */
  slug: string;
  description: string;
  isSystemDefault: boolean;
  createdAt: string;
  updatedAt: string;
  _count: {
    permissions: number;
    assignments: number;
  };
}

export interface RoleDetail extends RoleSummary {
  permissions: Permission[];
}

// --- List hook ---

export function useRoles() {
  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.get<RoleSummary[]>("/roles");
      setRoles(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load roles");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return { roles, isLoading, error, refetch: load };
}

// --- Detail hook ---

export function useRole(id: string | null) {
  const [role, setRole] = useState<RoleDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) { setRole(null); setIsLoading(false); return; }
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.get<RoleDetail>(`/roles/${id}`);
      setRole(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load role");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  // Clear stale role data synchronously when id changes so the detail
  // pane shows a loading state instead of briefly flashing the previous
  // role's permissions while the fetch is in flight.
  useEffect(() => {
    if (id) { setRole(null); setIsLoading(true); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  return { role, isLoading, error, refetch: load };
}

// --- Mutation hooks ---

export function useCreateRole() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const create = useCallback(async (data: { name: string; description?: string }) => {
    setIsSubmitting(true);
    try {
      return await api.post<RoleSummary>("/roles", data);
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return { create, isSubmitting };
}

export function useUpdateRole() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const update = useCallback(async (id: string, data: { name: string; description?: string }) => {
    setIsSubmitting(true);
    try {
      return await api.put<RoleDetail>(`/roles/${id}`, data);
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return { update, isSubmitting };
}

export function useDeleteRole() {
  const remove = useCallback(async (id: string) => {
    return api.delete(`/roles/${id}`);
  }, []);

  return { remove };
}

export function useAddPermission() {
  const add = useCallback(async (roleId: string, data: { action: string; stage: string; jobTypeId?: string }) => {
    return api.post<Permission>(`/roles/${roleId}/permissions`, data);
  }, []);

  return { add };
}

export function useRemovePermission() {
  const remove = useCallback(async (roleId: string, permissionId: string) => {
    return api.delete(`/roles/${roleId}/permissions/${permissionId}`);
  }, []);

  return { remove };
}
